import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { authFetch, isAuthenticated } from '../utils/auth';
import { toast } from 'sonner';
import { 
  Gift, CheckCircle2, AlertCircle, DollarSign, Package, 
  Plus, Minus, ShoppingCart, Truck, ArrowLeft, Loader2,
  Info, CreditCard, ShoppingBag
} from 'lucide-react';
import { getErrorMessage } from '../utils/errors';

const RedeemPoints = () => {
  const navigate = useNavigate();

  // Product image mapping — managed locally in the portal
  const PRODUCT_IMAGES = {
    'Pet Body Bags': '/assets/pet-body-bags.png',
    'Catheters — Packet': '/assets/catheters-multi.png',
    'Catheters — Pen': '/assets/catheters-sizes.png',
    'Catheters': '/assets/catheters-sizes.png',
    'IV Extensions': '/assets/iv-extensions.jpg',
    'Vetrap Cohesive Bandage': 'https://static.prod-images.emergentagent.com/jobs/241bcc82-0d91-4472-b673-d8d948e902d7/images/12e491903a74ef1a08518482f5a781ccb943dbf7cc9d71dd050aec3ffa5fbbb6.png',
  };

  // Size measurements for Pet Body Bags to help vets choose the right size
  const BAG_MEASUREMENTS = {
    'Small (100 units)':  '350 × 680 mm',
    'Medium (100 units)': '600 × 950 mm',
    'Large (100 units)':  '800 × 1200 mm',
  };
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [redemptionData, setRedemptionData] = useState(null);
  const [currentBalance, setCurrentBalance] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(true);
  const [redemptionType, setRedemptionType] = useState('cash');

  // Product state
  const [productCatalogue, setProductCatalogue] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [cart, setCart] = useState({});
  const [pointsToUse, setPointsToUse] = useState(null); // null = auto (use max available)
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [orderNotes, setOrderNotes] = useState('');

  // Cash state
  const [cashFormData, setCashFormData] = useState({
    points_to_redeem: '',
    notes: '',
    contact_email: '',
    account_name: '',
    bsb: '',
    account_number: '',
    save_bank_details: false
  });

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }
    fetchBalance();
  }, [navigate]);

  useEffect(() => {
    if (redemptionType === 'product' && productCatalogue.length === 0) {
      fetchProductCatalogue();
    }
  }, [redemptionType]);

  const fetchBalance = async () => {
    try {
      const response = await authFetch('/api/dashboard');
      if (response.ok) {
        const data = await response.json();
        setCurrentBalance(data);
        // Pre-fill bank details if saved
        if (data.bank_details) {
          setCashFormData(prev => ({
            ...prev,
            account_name: data.bank_details.account_name || '',
            bsb: data.bank_details.bsb || '',
            account_number: data.bank_details.account_number || ''
          }));
        }
      }
    } catch (error) {
      toast.error('Failed to load balance');
    } finally {
      setLoadingBalance(false);
    }
  };

  const fetchProductCatalogue = async () => {
    setLoadingProducts(true);
    try {
      const response = await authFetch('/api/product-catalogue');
      if (response.ok) {
        const data = await response.json();
        // Split any "Catheters" product with Packet/Pen variations into separate product cards
        const expanded = [];
        for (const product of (data.products || [])) {
          if (product.product === 'Catheters') {
            const packetVars = product.variations.filter(v => v.name.toLowerCase().includes('packet'));
            const penVars = product.variations.filter(v => v.name.toLowerCase().includes('pen'));
            const otherVars = product.variations.filter(v =>
              !v.name.toLowerCase().includes('packet') && !v.name.toLowerCase().includes('pen')
            );
            if (packetVars.length > 0) expanded.push({ product: 'Catheters — Packet', variations: packetVars });
            if (penVars.length > 0) expanded.push({ product: 'Catheters — Pen', variations: penVars });
            if (otherVars.length > 0) expanded.push({ product: 'Catheters', variations: otherVars });
          } else {
            expanded.push(product);
          }
        }
        // Custom display order: Body Bags, Vetrap, IV Extensions in row 1; Catheters in row 2
        const displayOrder = [
          'Pet Body Bags',
          'Vetrap Cohesive Bandage',
          'IV Extensions',
          'Catheters — Packet',
          'Catheters — Pen',
          'Catheters',
        ];
        expanded.sort((a, b) => {
          const ai = displayOrder.indexOf(a.product);
          const bi = displayOrder.indexOf(b.product);
          return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
        });
        setProductCatalogue(expanded);
      } else {
        toast.error('Failed to load product catalogue');
      }
    } catch (error) {
      toast.error('Failed to load product catalogue');
    } finally {
      setLoadingProducts(false);
    }
  };

  // Cart helpers
  const getCartKey = (product, variation) => `${product}|||${variation}`;

  const getCartQuantity = (product, variation) => {
    const key = getCartKey(product, variation);
    return cart[key]?.quantity || 0;
  };

  const updateCartQuantity = (product, variation, points, delta) => {
    const key = getCartKey(product, variation);
    setCart(prev => {
      const current = prev[key]?.quantity || 0;
      const newQty = Math.max(0, current + delta);
      if (newQty === 0) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return {
        ...prev,
        [key]: { product, variation, points, quantity: newQty }
      };
    });
  };

  const cartTotal = Object.values(cart).reduce(
    (sum, item) => sum + item.points * item.quantity, 0
  );

  const cartItemCount = Object.values(cart).reduce(
    (sum, item) => sum + item.quantity, 0
  );

  const availablePoints = currentBalance?.current_points || 0;
  
  // Split payment calculations
  const maxPointsForOrder = Math.min(availablePoints, cartTotal);
  const effectivePointsToUse = pointsToUse !== null ? Math.min(pointsToUse, maxPointsForOrder) : maxPointsForOrder;
  const cashOwing = Math.max(0, cartTotal - effectivePointsToUse);

  // Initialize pointsToUse when balance loads or cart changes
  useEffect(() => {
    if (pointsToUse === null && availablePoints > 0) {
      setPointsToUse(maxPointsForOrder);
    } else if (pointsToUse !== null && pointsToUse > maxPointsForOrder) {
      setPointsToUse(maxPointsForOrder);
    }
  }, [availablePoints, cartTotal]);

  // Submit cash redemption
  const handleCashSubmit = async (e) => {
    e.preventDefault();
    const points = parseInt(cashFormData.points_to_redeem);

    if (isNaN(points) || points < 5) {
      toast.error('Minimum redemption is 5 points ($5)');
      return;
    }
    if (currentBalance && points > currentBalance.current_points) {
      toast.error(`You only have ${currentBalance.current_points} points available`);
      return;
    }
    if (cashFormData.bsb) {
      const bsbPattern = /^\d{3}-?\d{3}$/;
      if (!bsbPattern.test(cashFormData.bsb)) {
        toast.error('Invalid BSB format. Expected: XXX-XXX (e.g., 123-456)');
        return;
      }
    }

    setLoading(true);
    try {
      const payload = {
        redemption_type: 'cash',
        points_to_redeem: points,
        notes: cashFormData.notes || null,
        contact_email: cashFormData.contact_email || null,
        account_name: cashFormData.account_name,
        bsb: cashFormData.bsb,
        account_number: cashFormData.account_number,
        save_bank_details: cashFormData.save_bank_details
      };

      const response = await authFetch('/api/redeem-points', {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (response.ok) {
        setRedemptionData({ ...data, type: 'cash' });
        setSuccess(true);
        toast.success('Cash redemption submitted successfully!');
        if (data.remaining_points !== undefined) {
          setCurrentBalance(prev => ({
            ...prev,
            current_points: data.remaining_points,
            cash_value: data.remaining_points
          }));
        }
      } else {
        toast.error(getErrorMessage(data.detail, 'Redemption failed'));
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Submit product order
  const handleProductSubmit = async () => {
    if (cartItemCount === 0) {
      toast.error('Please add at least one product to your order');
      return;
    }

    setLoading(true);
    try {
      const items = Object.values(cart).map(item => ({
        product: item.product.startsWith('Catheters —') ? 'Catheters' : item.product,
        variation: item.variation,
        quantity: item.quantity
      }));

      const payload = {
        items,
        points_to_use: effectivePointsToUse,
        delivery_address: deliveryAddress || undefined,
        notes: orderNotes || undefined
      };

      const response = await authFetch('/api/redeem-products', {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (response.ok) {
        setRedemptionData({ ...data, type: 'product', items });
        setSuccess(true);
        toast.success('Product order placed successfully!');
        if (data.remaining_points !== undefined) {
          setCurrentBalance(prev => ({
            ...prev,
            current_points: data.remaining_points,
            cash_value: data.remaining_points
          }));
        }
      } else {
        toast.error(getErrorMessage(data.detail, 'Product order failed'));
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSuccess(false);
    setRedemptionData(null);
    setCart({});
    setPointsToUse(null);
    setDeliveryAddress('');
    setOrderNotes('');
    setCashFormData({
      points_to_redeem: '',
      notes: '',
      contact_email: '',
      account_name: cashFormData.account_name,
      bsb: cashFormData.bsb,
      account_number: cashFormData.account_number,
      save_bank_details: false
    });
  };

  // Success confirmation view
  if (success && redemptionData) {
    return (
      <Layout>
        <div className="px-4 sm:px-6 lg:px-8 py-6 pb-20 md:pb-6">
          <div className="max-w-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
            >
              <Card className="rounded-2xl border-0 shadow-lg overflow-hidden">
                <div className="h-2 bg-[hsl(var(--success))]" />
                <CardContent className="p-8 text-center">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                    className="flex justify-center mb-6"
                  >
                    <div className="w-20 h-20 rounded-full bg-[hsl(var(--success-light))] flex items-center justify-center">
                      <CheckCircle2 className="text-[hsl(var(--success))]" size={40} />
                    </div>
                  </motion.div>

                  <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                    {redemptionData.type === 'product' ? 'Order Placed!' : 'Redemption Submitted!'}
                  </h2>
                  <p className="text-muted-foreground mb-6">
                    {redemptionData.type === 'product' 
                      ? 'Pure Souls will fulfil and dispatch your items.'
                      : 'Your cash redemption has been submitted for processing.'}
                  </p>

                  <div className="bg-muted/50 p-5 rounded-xl mb-6 text-left space-y-3">
                    {(redemptionData.order_id || redemptionData.redemption_id) && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Reference</span>
                        <span className="font-mono text-sm font-medium" data-testid="order-id-value">
                          #{(redemptionData.order_id || redemptionData.redemption_id).substring(0, 8).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <Separator />
                    {redemptionData.type === 'product' && redemptionData.total_value !== undefined && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Order Total</span>
                        <span className="font-semibold">${redemptionData.total_value}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Points Used</span>
                      <span className="font-bold text-lg text-primary" data-testid="points-deducted-value">
                        {redemptionData.points_used || redemptionData.total_points_deducted || redemptionData.points_redeemed} pts
                      </span>
                    </div>
                    {redemptionData.cash_outstanding > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Cash Owing</span>
                        <Badge className="bg-[hsl(var(--warning-light))] text-[hsl(var(--warning))] border-[hsl(var(--warning))] border font-semibold px-3">
                          ${redemptionData.cash_outstanding.toFixed(2)}
                        </Badge>
                      </div>
                    )}
                    {redemptionData.cash_value !== undefined && redemptionData.type === 'cash' && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Cash Value</span>
                        <span className="font-semibold">${redemptionData.cash_value?.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Remaining Balance</span>
                      <span className="font-semibold" data-testid="remaining-balance-value">
                        {redemptionData.remaining_points} pts (${redemptionData.remaining_points})
                      </span>
                    </div>
                  </div>

                  {/* Cash invoice notice */}
                  {redemptionData.cash_outstanding > 0 && (
                    <div className="bg-[hsl(var(--warning-light))] border border-[hsl(var(--warning))]/20 p-4 rounded-xl mb-4">
                      <p className="text-sm text-[hsl(var(--warning))] font-medium">
                        <DollarSign size={14} className="inline mr-1.5 -mt-0.5" />
                        You'll receive an invoice for the remaining ${redemptionData.cash_outstanding.toFixed(2)}
                      </p>
                    </div>
                  )}

                  {/* Product order items summary */}
                  {redemptionData.type === 'product' && redemptionData.items && (
                    <div className="bg-primary/5 p-4 rounded-xl mb-6 text-left">
                      <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                        <Package size={16} className="text-primary" />
                        Order Items
                      </h4>
                      <div className="space-y-2">
                        {redemptionData.items.map((item, idx) => (
                          <div key={`${item.product}-${item.variation}-${idx}`} className="flex justify-between text-sm">
                            <span>{item.quantity}x {item.product} ({item.variation})</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="bg-[hsl(var(--info-light))] border border-[hsl(var(--info))]/20 p-4 rounded-xl mb-6">
                    <p className="text-sm text-[hsl(var(--info))]">
                      <Info size={14} className="inline mr-1.5 -mt-0.5" />
                      {redemptionData.type === 'product'
                        ? "You'll receive a notification when your order has been dispatched."
                        : 'Processing time: 1-3 business days. You will receive a confirmation email.'}
                    </p>
                  </div>

                  <div className="flex gap-3 justify-center">
                    <Button 
                      onClick={() => navigate('/dashboard')}
                      data-testid="go-to-dashboard-button"
                    >
                      Go to Dashboard
                    </Button>
                    <Button
                      variant="outline"
                      onClick={resetForm}
                      data-testid="redeem-more-button"
                    >
                      Redeem More Points
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 pb-20 md:pb-6" data-testid="redeem-points-page">
        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-3xl md:text-4xl font-bold text-primary" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Redeem Points
          </h1>
          <p className="text-muted-foreground mt-1">
            Convert your reward points to cash or products
          </p>
        </motion.div>

        {/* Current Balance Card */}
        {!loadingBalance && currentBalance && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            <Card className="rounded-2xl border-0 shadow-md overflow-hidden">
              <div className="h-1.5 bg-gradient-to-r from-primary to-primary/60" />
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Your Current Balance</p>
                    <p className="text-4xl font-bold text-primary" style={{ fontFamily: 'Fredoka, sans-serif' }} data-testid="balance-points-value">
                      {currentBalance.current_points} pts
                    </p>
                    <p className="text-lg text-muted-foreground mt-1">
                      = <span className="font-semibold">${currentBalance.current_points}</span> value
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">1 point = $1</p>
                  </div>
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                    <Gift className="text-primary" size={32} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {loadingBalance && (
          <div className="h-32 rounded-2xl bg-muted/50 animate-pulse" />
        )}

        {/* Redemption Type Selector */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <Card className="rounded-2xl border-0 shadow-md">
            <CardHeader className="pb-4">
              <CardTitle className="text-xl" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                Choose Redemption Type
              </CardTitle>
            </CardHeader>
            <CardContent>
              <RadioGroup
                value={redemptionType}
                onValueChange={setRedemptionType}
                className="grid grid-cols-1 md:grid-cols-2 gap-4"
                data-testid="redemption-type-radio"
              >
                <div 
                  className={`flex items-center space-x-3 border-2 rounded-xl p-5 cursor-pointer transition-all duration-200 ${
                    redemptionType === 'cash' 
                      ? 'border-primary bg-primary/5 shadow-sm' 
                      : 'border-border hover:border-primary/30 hover:bg-muted/50'
                  }`}
                  onClick={() => setRedemptionType('cash')}
                >
                  <RadioGroupItem value="cash" id="cash" />
                  <Label htmlFor="cash" className="cursor-pointer flex-1">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[hsl(var(--success))]/10 flex items-center justify-center">
                        <CreditCard className="text-[hsl(var(--success))]" size={20} />
                      </div>
                      <div>
                        <div className="font-semibold">Cash Redemption</div>
                        <div className="text-sm text-muted-foreground">
                          Receive payment via bank transfer
                        </div>
                      </div>
                    </div>
                  </Label>
                </div>

                <div 
                  className={`flex items-center space-x-3 border-2 rounded-xl p-5 cursor-pointer transition-all duration-200 ${
                    redemptionType === 'product' 
                      ? 'border-primary bg-primary/5 shadow-sm' 
                      : 'border-border hover:border-primary/30 hover:bg-muted/50'
                  }`}
                  onClick={() => setRedemptionType('product')}
                >
                  <RadioGroupItem value="product" id="product" />
                  <Label htmlFor="product" className="cursor-pointer flex-1">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <ShoppingBag className="text-primary" size={20} />
                      </div>
                      <div>
                        <div className="font-semibold">Product Redemption</div>
                        <div className="text-sm text-muted-foreground">
                          Order Pure Souls products
                        </div>
                      </div>
                    </div>
                  </Label>
                </div>
              </RadioGroup>
            </CardContent>
          </Card>
        </motion.div>

        {/* Cash Redemption Form */}
        <AnimatePresence mode="wait">
          {redemptionType === 'cash' && (
            <motion.div
              key="cash-form"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="rounded-2xl border-0 shadow-md">
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Fredoka, sans-serif' }}>Cash Redemption Details</CardTitle>
                  <CardDescription>Enter the amount and bank details for your redemption</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleCashSubmit} className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="points">Points to Redeem *</Label>
                      <Input
                        id="points"
                        type="number"
                        min="5"
                        placeholder="Enter number of points (minimum 5)"
                        value={cashFormData.points_to_redeem}
                        onChange={(e) => setCashFormData(prev => ({ ...prev, points_to_redeem: e.target.value }))}
                        required
                        data-testid="cash-points-input"
                        className="rounded-lg"
                      />
                      {cashFormData.points_to_redeem && (
                        <p className="text-sm text-muted-foreground">
                          = <span className="font-semibold">${parseInt(cashFormData.points_to_redeem || 0)}</span> cash value
                        </p>
                      )}
                    </div>

                    {/* Bank Details */}
                    <div className="space-y-4 p-5 bg-primary/5 rounded-xl border border-primary/20">
                      <div className="flex items-center gap-2 mb-1">
                        <CreditCard size={16} className="text-primary" />
                        <h4 className="font-semibold text-sm">Bank Account Details</h4>
                        <Badge variant="outline" className="text-xs">Required</Badge>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="account_name">Account Name *</Label>
                        <Input
                          id="account_name"
                          value={cashFormData.account_name}
                          onChange={(e) => setCashFormData(prev => ({ ...prev, account_name: e.target.value }))}
                          required
                          data-testid="account-name-input"
                          className="rounded-lg"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="bsb">BSB *</Label>
                          <Input
                            id="bsb"
                            value={cashFormData.bsb}
                            onChange={(e) => setCashFormData(prev => ({ ...prev, bsb: e.target.value }))}
                            maxLength={7}
                            required
                            data-testid="bsb-input"
                            className="rounded-lg"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="account_number">Account Number *</Label>
                          <Input
                            id="account_number"
                            value={cashFormData.account_number}
                            onChange={(e) => setCashFormData(prev => ({ ...prev, account_number: e.target.value }))}
                            required
                            data-testid="account-number-input"
                            className="rounded-lg"
                          />
                        </div>
                      </div>

                      <div className="flex items-center space-x-2 pt-1">
                        <input
                          type="checkbox"
                          id="save_bank_details"
                          checked={cashFormData.save_bank_details}
                          onChange={(e) => setCashFormData(prev => ({ ...prev, save_bank_details: e.target.checked }))}
                          className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                        />
                        <Label htmlFor="save_bank_details" className="cursor-pointer text-sm">
                          Save bank details for future redemptions
                        </Label>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="notes">Notes (Optional)</Label>
                      <Textarea
                        id="notes"
                        placeholder="Any special instructions or notes..."
                        value={cashFormData.notes}
                        onChange={(e) => setCashFormData(prev => ({ ...prev, notes: e.target.value }))}
                        rows={3}
                        data-testid="notes-textarea"
                        className="rounded-lg resize-none"
                      />
                    </div>

                    <div className="bg-[hsl(var(--warning-light))] border border-[hsl(var(--warning))]/20 p-4 rounded-xl flex gap-3">
                      <AlertCircle className="text-[hsl(var(--warning))] flex-shrink-0 mt-0.5" size={18} />
                      <div className="text-sm text-[hsl(var(--warning))]">
                        <p className="font-medium mb-1">Important:</p>
                        <ul className="list-disc list-inside space-y-0.5">
                          <li>Minimum redemption: 5 points ($5)</li>
                          <li>Points will be deducted immediately</li>
                          <li>Processing time: 1-3 business days</li>
                        </ul>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <Button
                        type="submit"
                        disabled={loading || loadingBalance}
                        data-testid="cash-submit-button"
                        className="flex-1 rounded-xl h-12 font-semibold"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="mr-2 animate-spin" size={18} />
                            Processing...
                          </>
                        ) : (
                          <>
                            <DollarSign className="mr-2" size={18} />
                            Submit Cash Redemption
                          </>
                        )}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => navigate('/dashboard')}
                        className="rounded-xl"
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Product Redemption */}
          {redemptionType === 'product' && (
            <motion.div
              key="product-form"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="space-y-6"
            >
              {/* Product Catalogue */}
              {loadingProducts ? (
                <div className="space-y-4">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="h-48 rounded-2xl bg-muted/50 animate-pulse" />
                  ))}
                </div>
              ) : productCatalogue.length === 0 ? (
                <Card className="rounded-2xl border-0 shadow-md">
                  <CardContent className="flex flex-col items-center justify-center py-16">
                    <Package className="h-12 w-12 text-muted-foreground mb-4" />
                    <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                      No Products Available
                    </h3>
                    <p className="text-muted-foreground text-center mb-4">
                      The product catalogue is currently empty. Please check back later.
                    </p>
                    <Button variant="outline" onClick={fetchProductCatalogue}>
                      Retry
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-semibold" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                      Product Catalogue
                    </h2>
                    <Badge variant="outline" className="text-xs">
                      {productCatalogue.length} products available
                    </Badge>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {productCatalogue.map((product, productIdx) => {
                    const productImage = PRODUCT_IMAGES[product.product];
                    const totalQtyForProduct = product.variations.reduce(
                      (sum, v) => sum + getCartQuantity(product.product, v.name), 0
                    );

                    return (
                      <motion.div
                        key={product.product}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: productIdx * 0.1 }}
                      >
                        <Card className={`rounded-2xl border-0 shadow-md overflow-hidden transition-all duration-300 hover:shadow-xl ${
                          totalQtyForProduct > 0 ? 'ring-2 ring-primary/30' : ''
                        }`} data-testid={`product-card-${productIdx}`}>
                          {/* Product Image */}
                          <div className={`relative h-48 md:h-52 overflow-hidden ${
                            product.product === 'Pet Body Bags' || product.product === 'IV Extensions'
                              ? 'bg-muted/10'
                              : 'bg-white'
                          }`}>
                            {productImage ? (
                              <img
                                src={productImage}
                                alt={product.product}
                                className={`w-full h-full transition-transform duration-500 hover:scale-105 ${
                                  product.product === 'Pet Body Bags' || product.product === 'IV Extensions'
                                    ? 'object-cover'
                                    : 'object-contain p-3'
                                }`}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-primary/5">
                                <Package className="text-primary/30" size={48} />
                              </div>
                            )}
                            {totalQtyForProduct > 0 && (
                              <div className="absolute top-3 right-3 bg-primary text-white text-xs font-bold rounded-full w-7 h-7 flex items-center justify-center shadow-lg">
                                {totalQtyForProduct}
                              </div>
                            )}
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 to-transparent p-4 pt-8">
                              <h3 className="text-white text-lg font-bold drop-shadow-sm" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                                {product.product}
                              </h3>
                            </div>
                          </div>

                          {/* Variations */}
                          <CardContent className="p-4">
                            <div className="space-y-2">
                              {product.variations.map((variation, varIdx) => {
                                const qty = getCartQuantity(product.product, variation.name);
                                const lineTotal = qty * variation.points;
                                return (
                                  <div
                                    key={variation.name}
                                    className={`flex items-center justify-between gap-2 p-2.5 rounded-xl border transition-all duration-200 ${
                                      qty > 0
                                        ? 'border-primary/30 bg-primary/5'
                                        : 'border-border hover:border-primary/20 hover:bg-muted/30'
                                    }`}
                                    data-testid={`variation-row-${productIdx}-${varIdx}`}
                                  >
                                    <div className="flex-1 min-w-0">
                                      <p className="text-xs font-medium truncate">{variation.name}</p>
                                      {product.product === 'Pet Body Bags' && BAG_MEASUREMENTS[variation.name] && (
                                        <p className="text-[10px] text-muted-foreground font-mono mt-0.5">{BAG_MEASUREMENTS[variation.name]}</p>
                                      )}
                                      <div className="flex items-center gap-2 mt-0.5">
                                        <span className="text-sm text-primary font-bold">{variation.points} pts</span>
                                        <span className="text-[10px] text-muted-foreground">(${variation.points})</span>
                                        {qty > 0 && (
                                          <span className="text-xs font-semibold text-primary ml-auto whitespace-nowrap">
                                            = {lineTotal} pts
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-0.5 bg-muted/50 rounded-lg p-0.5 flex-shrink-0">
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-7 w-7 rounded-md hover:bg-background"
                                        onClick={() => updateCartQuantity(product.product, variation.name, variation.points, -1)}
                                        disabled={qty === 0}
                                        data-testid={`decrease-${productIdx}-${varIdx}`}
                                      >
                                        <Minus size={12} />
                                      </Button>
                                      <span className="w-6 text-center font-semibold text-xs" data-testid={`quantity-${productIdx}-${varIdx}`}>
                                        {qty}
                                      </span>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-7 w-7 rounded-md hover:bg-background"
                                        onClick={() => updateCartQuantity(product.product, variation.name, variation.points, 1)}
                                        data-testid={`increase-${productIdx}-${varIdx}`}
                                      >
                                        <Plus size={12} />
                                      </Button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    );
                  })}
                  </div>

                  {/* Optional fields */}
                  <Card className="rounded-2xl border-0 shadow-md">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                        Delivery Details (Optional)
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="delivery-address">Delivery Address</Label>
                        <Input
                          id="delivery-address"
                          placeholder="Leave blank to use your clinic address"
                          value={deliveryAddress}
                          onChange={(e) => setDeliveryAddress(e.target.value)}
                          data-testid="delivery-address-input"
                          className="rounded-lg"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="order-notes">Order Notes</Label>
                        <Textarea
                          id="order-notes"
                          placeholder="Any special instructions..."
                          value={orderNotes}
                          onChange={(e) => setOrderNotes(e.target.value)}
                          rows={2}
                          data-testid="order-notes-textarea"
                          className="rounded-lg resize-none"
                        />
                      </div>
                    </CardContent>
                  </Card>

                  {/* Payment Breakdown & Place Order */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="sticky bottom-20 md:bottom-4 z-20"
                  >
                    <Card className="rounded-2xl border-2 border-primary/40 bg-card shadow-lg" style={{ boxShadow: '0 0 20px rgba(13, 148, 136, 0.12), 0 4px 12px rgba(0,0,0,0.08)' }}>
                      <CardContent className="p-5 space-y-4">
                        {/* Points slider — only show when cart has items */}
                        {cartItemCount > 0 && (
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <Label className="text-base font-bold">Apply Points</Label>
                              <span className="text-sm font-medium text-muted-foreground">
                                {availablePoints} pts available
                              </span>
                            </div>
                            <div className="flex items-center gap-4">
                              <style>{`
                                .points-slider {
                                  -webkit-appearance: none;
                                  appearance: none;
                                  height: 10px;
                                  border-radius: 8px;
                                  outline: none;
                                  cursor: pointer;
                                }
                                .points-slider::-webkit-slider-thumb {
                                  -webkit-appearance: none;
                                  appearance: none;
                                  width: 28px;
                                  height: 28px;
                                  border-radius: 50%;
                                  background: linear-gradient(135deg, #0d9488, #0d7377);
                                  border: 3px solid white;
                                  box-shadow: 0 0 12px rgba(13, 115, 119, 0.5), 0 2px 8px rgba(0,0,0,0.15);
                                  cursor: pointer;
                                  transition: box-shadow 0.2s, transform 0.2s;
                                }
                                .points-slider::-webkit-slider-thumb:hover {
                                  box-shadow: 0 0 20px rgba(13, 115, 119, 0.7), 0 2px 12px rgba(0,0,0,0.2);
                                  transform: scale(1.1);
                                }
                                .points-slider::-moz-range-thumb {
                                  width: 28px;
                                  height: 28px;
                                  border-radius: 50%;
                                  background: linear-gradient(135deg, #0d9488, #0d7377);
                                  border: 3px solid white;
                                  box-shadow: 0 0 12px rgba(13, 115, 119, 0.5), 0 2px 8px rgba(0,0,0,0.15);
                                  cursor: pointer;
                                }
                              `}</style>
                              <input
                                type="range"
                                min={0}
                                max={maxPointsForOrder}
                                value={effectivePointsToUse}
                                onChange={(e) => setPointsToUse(parseInt(e.target.value))}
                                className="points-slider flex-1"
                                style={{
                                  background: `linear-gradient(to right, #0d9488 0%, #0d7377 ${maxPointsForOrder > 0 ? (effectivePointsToUse / maxPointsForOrder) * 100 : 0}%, #e2e8f0 ${maxPointsForOrder > 0 ? (effectivePointsToUse / maxPointsForOrder) * 100 : 0}%, #e2e8f0 100%)`,
                                }}
                                data-testid="points-slider"
                              />
                              <div className="flex items-center gap-1 bg-primary/10 border border-primary/20 rounded-xl px-4 py-2 min-w-[90px] justify-center shadow-sm">
                                <span className="text-base font-bold text-primary" data-testid="points-slider-value">
                                  {effectivePointsToUse}
                                </span>
                                <span className="text-xs font-medium text-primary/70">pts</span>
                              </div>
                            </div>

                            {/* Payment breakdown */}
                            <div className="flex items-center justify-between bg-muted/30 rounded-xl p-3 text-sm">
                              <div className="flex items-center gap-4">
                                <div className="flex items-center gap-1.5">
                                  <Gift size={14} className="text-primary" />
                                  <span className="font-semibold text-primary">{effectivePointsToUse} pts</span>
                                </div>
                                {cashOwing > 0 && (
                                  <>
                                    <span className="text-muted-foreground">+</span>
                                    <div className="flex items-center gap-1.5">
                                      <DollarSign size={14} className="text-[hsl(var(--warning))]" />
                                      <span className="font-semibold text-[hsl(var(--warning))]">${cashOwing.toFixed(2)} cash</span>
                                    </div>
                                  </>
                                )}
                              </div>
                              <span className="text-muted-foreground font-medium">
                                Total: ${cartTotal}
                              </span>
                            </div>

                            {cashOwing > 0 && (
                              <p className="text-xs text-[hsl(var(--warning))] flex items-center gap-1.5">
                                <Info size={12} />
                                You'll receive an invoice for the remaining ${cashOwing.toFixed(2)}
                              </p>
                            )}
                          </div>
                        )}

                        {/* Order summary row + button */}
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-1">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                              <ShoppingCart className="text-primary" size={24} />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="text-2xl font-bold" style={{ fontFamily: 'Fredoka, sans-serif' }} data-testid="cart-total-points">
                                  ${cartTotal}
                                </span>
                              </div>
                              <p className="text-sm text-muted-foreground">
                                {cartItemCount === 0 
                                  ? 'No items in cart' 
                                  : `${cartItemCount} item${cartItemCount > 1 ? 's' : ''} selected`}
                              </p>
                            </div>
                          </div>

                          <Button
                            onClick={handleProductSubmit}
                            disabled={loading || cartItemCount === 0}
                            className="rounded-xl h-12 px-8 font-semibold whitespace-nowrap"
                            data-testid="place-order-button"
                          >
                            {loading ? (
                              <>
                                <Loader2 className="mr-2 animate-spin" size={18} />
                                Placing Order...
                              </>
                            ) : cartItemCount === 0 ? (
                              <>
                                <Truck className="mr-2" size={18} />
                                Place Order
                              </>
                            ) : cashOwing > 0 ? (
                              <>
                                <Truck className="mr-2" size={18} />
                                Place Order — {effectivePointsToUse} pts + ${cashOwing.toFixed(2)}
                              </>
                            ) : (
                              <>
                                <Truck className="mr-2" size={18} />
                                Place Order — {effectivePointsToUse} pts
                              </>
                            )}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Layout>
  );
};

export default RedeemPoints;
